import { ArrowLeft, ArrowRight, Crosshair, MessageSquareQuote, Search } from 'lucide-react';
import { CASE_001, characterById } from '../case/case001';
import { shortHash } from '../engine/yuragi';
import { useGame } from '../game/GameContext';

export function DialogueOverlay() {
  const { state, dispatch } = useGame();
  const character = state.activeCharacterId ? characterById[state.activeCharacterId] : null;
  const statement = CASE_001.statements[state.activeStatementIndex];
  if (!character) return null;
  const isTrial = state.phase === 'trial';
  const pressed = statement && state.pressedStatementIds.includes(statement.id);
  const pressureLevel = statement ? state.pressureByStatement[statement.id] ?? 0 : 0;
  const pressureLine = statement && pressureLevel > 0
    ? statement.pressureLines[Math.min(pressureLevel, 3) - 1]
    : null;
  const contradicted = statement && state.contradictionsFound.includes(statement.id);
  const lastAction = state.actionLog.at(-1);
  const wrong = lastAction?.type === 'wrong-presentation' && lastAction.target === statement?.id;
  const agent = state.yuragi.agents[character.id];
  const memoryCount = Object.keys(agent.knowledge).length;

  return (
    <section className={`dialogue ${isTrial ? 'dialogue--trial' : ''} screen-layer`}>
      <div className="dialogue__portrait"><img src={character.portrait} alt={character.name} /><i style={{ background: character.accent }} /></div>
      <div className="dialogue__content panel-surface">
        <header>
          <div><span>{character.shortRole}</span><h2>{character.name}</h2><small>{character.role}</small></div>
          {!isTrial && <button className="text-action" onClick={() => dispatch({ type: 'CLOSE_OVERLAY' })}>Return to scene <ArrowRight /></button>}
          {isTrial && <div className="dialogue__trial-tools"><button className="text-action" onClick={() => dispatch({ type: 'CLOSE_OVERLAY' })}>Step into court <ArrowRight /></button><div className="testimony-count">TESTIMONY {state.activeStatementIndex + 1} / {CASE_001.statements.length}</div></div>}
        </header>

        <div className="agent-readout">
          <span><i /> YURAGI AGENT · TICK {agent.state.n}</span>
          <strong>{agent.decision} · {agent.stance}</strong>
          <small>AROUSAL {Math.round(agent.arousal * 100)} · TRUST {Math.round(agent.trust * 100)} · MEMORY {memoryCount} · {shortHash(agent.stateHash)}</small>
        </div>

        {isTrial ? (
          <>
            <div className="statement">
              <MessageSquareQuote />
              <blockquote>{statement.text}</blockquote>
            </div>
            {pressed && (
              <div className="pressure-line">
                <div><span>PRESSURE {pressureLevel}/3</span><i>{[1, 2, 3].map((level) => <b key={level} className={level <= pressureLevel ? 'is-filled' : ''} />)}</i></div>
                <p>{pressureLine}</p>
              </div>
            )}
            {contradicted && <div className="contradiction-result is-correct"><strong>{statement.contradictionTitle}</strong><p>{statement.contradictionText}</p></div>}
            {wrong && !contradicted && <div className="contradiction-result is-wrong"><strong>Objection overruled.</strong><p>The exhibit does not contradict this claim. One credibility mark lost.</p></div>}
            <footer className="dialogue__actions">
              <button className="secondary-action" disabled={state.activeStatementIndex === 0} onClick={() => dispatch({ type: 'SET_STATEMENT', index: state.activeStatementIndex - 1 })}><ArrowLeft /> Previous</button>
              <button className="secondary-action" onClick={() => dispatch({ type: 'PRESS_STATEMENT', statementId: statement.id })}><Search /> {pressureLevel === 0 ? 'Press witness' : pressureLevel < 3 ? `Press harder · ${pressureLevel + 1}/3` : 'Review pressure · 3/3'}</button>
              <button className="primary-action" onClick={() => dispatch({ type: 'VIEW_EVIDENCE', evidenceId: state.discoveredEvidenceIds.at(-1) ?? 'charge-sheet' })}><Crosshair /> Present evidence</button>
              <button className="secondary-action" disabled={state.activeStatementIndex === CASE_001.statements.length - 1} onClick={() => dispatch({ type: 'SET_STATEMENT', index: state.activeStatementIndex + 1 })}>Next <ArrowRight /></button>
            </footer>
          </>
        ) : (
          <>
            <p className="dialogue__summary">{character.summary}</p>
            <div className="dialogue__tell"><span>OBSERVED TELL</span><p>{character.tell}</p></div>
            {statement && (
              <div className="field-question">
                <span>Ask about the timeline</span>
                <blockquote>“{statement.text}”</blockquote>
                {pressed && <p>{pressureLine}</p>}
                <div className="field-question__pressure">
                  <i>{[1, 2, 3].map((level) => <b key={level} className={level <= pressureLevel ? 'is-filled' : ''} />)}</i>
                  <button className="secondary-action" onClick={() => dispatch({ type: 'PRESS_STATEMENT', statementId: statement.id })}><Search /> {pressureLevel === 0 ? 'Apply pressure' : pressureLevel < 3 ? `Question again · ${pressureLevel + 1}/3` : 'Statement committed · 3/3'}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
